/**
 * Contact Page - Rebuilt for logistics theme and agency UI standards
 */
import React from 'react';
import { motion } from 'framer-motion';
import {
  FaMapMarkerAlt, FaPhone, FaEnvelope, FaGlobe,
  FaLinkedinIn, FaFacebookF, FaInstagram, FaYoutube,
  FaClock, FaWhatsapp, FaAnchor, FaShip, FaBuilding
} from 'react-icons/fa';
import SEO from '../components/ui/SEO';
import { getLocalBusinessSchema, getBreadcrumbSchema } from '../utils/schema';
import AnimatedSection, { StaggerContainer, StaggerItem } from '../components/ui/AnimatedSection';

const contactInfo = [
  {
    icon: FaMapMarkerAlt,
    title: 'Our Location',
    content: 'SF. No.156/1, Plot 3/436-66, Isaac Street, Central State Govt Employee Colony, Kumaragiri, Pudukkottai, Thoothukudi - 628103, Tamil Nadu, India.',
    color: 'var(--orange)'
  },
  {
    icon: FaPhone,
    title: 'Phone',
    content: '+91 70107 55885',
    link: 'tel:+917010755885',
    color: 'var(--orange)'
  },
  {
    icon: FaEnvelope,
    title: 'Email',
    content: 'info@isaacgroup.net',
    link: 'mailto:info@isaacgroup.net',
    color: 'var(--orange)'
  },
  {
    icon: FaGlobe,
    title: 'Website',
    content: 'www.isaacgroup.net',
    link: 'https://www.isaacgroup.net',
    color: 'var(--orange)'
  }
];

const socialLinks = [
  { icon: FaLinkedinIn, label: 'LinkedIn', url: 'https://linkedin.com' },
  { icon: FaFacebookF, label: 'Facebook', url: 'https://facebook.com' },
  { icon: FaInstagram, label: 'Instagram', url: 'https://instagram.com' },
  { icon: FaYoutube, label: 'YouTube', url: 'https://youtube.com' }
];

const businessHours = [
  { label: 'Monday - Friday', hours: '9:30 AM - 6:00 PM' },
  { label: 'Saturday (1st, 3rd & 5th)', hours: '10:00 AM - 02:00 PM' },
  { label: 'Saturday (2nd & 4th)', hours: 'Closed' },
  { label: 'Sunday', hours: 'Closed' }
];

const operationalHubs = [
  {
    city: 'Thoothukudi (Tuticorin)',
    role: 'Corporate Headquarters & Operations Base',
    address: 'SF. No.156/1, Plot 3/436-66, Isaac Street, Central State Govt Employee Colony, Kumaragiri, Pudukkottai, Thoothukudi - 628103, Tamil Nadu, India.',
    email: 'info@isaacgroup.net',
    phone: '+91 70107 55885',
    icon: FaAnchor,
    color: 'var(--orange)',
    dim: 'var(--orange-dim)',
    border: 'var(--orange-border)'
  },
  {
    city: 'Vizhinjam (Trivandrum)',
    role: 'Deepwater Transshipment Port Agency Desk',
    address: 'Adani Vizhinjam Port Terminal Area, Vizhinjam, Trivandrum, Kerala, India.',
    email: 'ops.trv@isaacgroup.net',
    phone: '+91 70107 55885',
    icon: FaShip,
    color: 'var(--purple-accent)',
    dim: 'var(--purple-accent-dim)',
    border: 'var(--purple-accent-border)'
  },
  {
    city: 'Chennai',
    role: 'Regional Logistics & Freight Forwarding Gateway',
    address: 'Chennai Port & Kamarajar Port (Ennore) Operations Division, Chennai, Tamil Nadu, India.',
    email: 'ops.maa@isaacgroup.net',
    phone: '+91 70107 55885',
    icon: FaBuilding,
    color: 'var(--teal)',
    dim: 'var(--teal-dim)',
    border: 'rgba(18, 122, 130, 0.3)'
  }
];

const Contact = () => {
  const breadcrumbs = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.isaacgroup.net/' },
    { name: 'Contact', url: 'https://www.isaacgroup.net/contact' }
  ]);

  return (
    <main style={{ paddingTop: '72px' }}>
      <SEO 
        title="Contact ISAAC Logistics | Thoothukudi"
        description="Get in touch with ISAAC Logistic Solutions India Pvt. Ltd. in Thoothukudi, Tamil Nadu. Reach our 24/7 port operations team for shipping agency, freight forwarding, and project cargo inquiries."
        canonicalUrl="https://www.isaacgroup.net/contact"
        schema={[getLocalBusinessSchema(), breadcrumbs]}
      />
      {/* Page Hero */}
      <section style={{
        padding: '6rem 0',
        background: 'var(--navy-deep)',
        position: 'relative',
        overflow: 'hidden',
        borderBottom: '1px solid var(--orange-border)'
      }}>
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: '15%', width: '1px',
        background: 'rgba(255,255,255,0.02)', pointerEvents: 'none'
      }} />
      <div className="container-xxl px-4 px-lg-5" style={{ position: 'relative', zIndex: 1 }}>
        <AnimatedSection variant="fadeUp">
          <span className="section-tag">Get In Touch</span>
          <h1 style={{ color: '#fff', marginTop: '0.75rem', marginBottom: '1.2rem', letterSpacing: '-0.025em', fontWeight: 800 }}>
            Contact <span style={{ color: 'var(--orange)' }}>ISAAC Group</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', maxWidth: '600px', lineHeight: '1.8', fontSize: '1rem', margin: 0, textAlign: 'justify' }}>
            Reach out to our operations team for vessel calls, freight agency bookings, and project cargo inquiries. We are active 24/7.
          </p>
        </AnimatedSection>
      </div>
    </section>

    {/* Contact Cards Grid */}
    <section style={{ padding: '8rem 0', background: 'var(--bg-page)', borderBottom: '1px solid rgba(10,17,40,0.05)' }}>
      <div className="container-xxl px-4 px-lg-5">
        <StaggerContainer className="row g-4 mb-5" staggerDelay={0.06}>
          {contactInfo.map((c, i) => (
            <StaggerItem key={i} className="col-sm-6 col-lg-3">
              <motion.div
                whileHover={{ y: -5 }}
                transition={{ duration: 0.25 }}
                style={{
                  background: '#FFFFFF',
                  borderRadius: 2,
                  padding: '2.5rem 2rem',
                  height: '100%',
                  border: '1px solid rgba(10,17,40,0.04)',
                  boxShadow: 'var(--shadow-sm)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  transition: 'border-color 0.2s'
                }}
                className="contact-card-item"
              >
                <div style={{
                  width: '36px', height: '36px', borderRadius: 2,
                  background: 'var(--orange-dim)', border: '1px solid var(--orange-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '1.5rem'
                }}>
                  <c.icon size={14} color="var(--orange)" />
                </div>
                <h5 style={{
                  fontFamily: 'var(--font-heading)', fontWeight: 700,
                  color: 'var(--navy)', fontSize: '0.92rem', marginBottom: '0.60rem',
                  letterSpacing: '-0.01em'
                }}>
                  {c.title}
                </h5>
                {c.link ? (
                  <a href={c.link} style={{
                    color: 'var(--text-body)', fontSize: '0.82rem', textDecoration: 'none',
                    lineHeight: '1.6', display: 'block', transition: 'color 0.2s'
                  }} className="contact-link-tag">
                    {c.content}
                  </a>
                ) : (
                  <p style={{ color: 'var(--text-body)', fontSize: '0.82rem', lineHeight: '1.65', margin: 0, textAlign: 'justify' }}>
                    {c.content}
                  </p>
                )}
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        {/* Operational Port Hubs */}
        <AnimatedSection variant="fadeUp" className="mb-5 mt-5">
          <div style={{ borderTop: '1px solid rgba(10,17,40,0.06)', paddingTop: '4rem', marginBottom: '2.5rem' }}>
            <span className="section-tag">Our Active Gateways</span>
            <h3 style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 800,
              color: 'var(--navy)',
              fontSize: '1.8rem',
              letterSpacing: '-0.02em',
              marginTop: '0.5rem',
              marginBottom: '0.8rem'
            }}>
              Operational Port Hubs
            </h3>
            <p style={{ color: 'var(--text-body)', fontSize: '0.9rem', maxWidth: '600px', lineHeight: 1.6, textAlign: 'justify' }}>
              ISAAC Logistics is active 24/7 at India's key maritime checkpoints. Our local desk manages all vessel clearances and transport logistics directly:
            </p>
          </div>
        </AnimatedSection>

        <StaggerContainer className="row g-4 mb-5" staggerDelay={0.06}>
          {operationalHubs.map((hub, i) => {
            const IconComp = hub.icon;
            return (
              <StaggerItem key={i} className="col-lg-4">
                <motion.div
                  whileHover={{ y: -4, borderColor: hub.color }}
                  transition={{ duration: 0.25 }}
                  style={{
                    background: '#FFFFFF',
                    borderRadius: 2,
                    padding: '2.5rem 2rem',
                    height: '100%',
                    border: '1px solid rgba(10,17,40,0.04)',
                    borderTop: `3px solid ${hub.color}`,
                    boxShadow: 'var(--shadow-sm)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    transition: 'border-color 0.2s'
                  }}
                  className="contact-card-item"
                >
                  <div>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: 2,
                      background: hub.dim, border: `1px solid ${hub.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: '1.5rem'
                    }}>
                      <IconComp size={14} color={hub.color === 'var(--orange)' ? 'var(--orange)' : hub.color === 'var(--purple-accent)' ? 'var(--purple-accent-light)' : 'var(--teal-light)'} />
                    </div>
                    <h4 style={{
                      fontFamily: 'var(--font-heading)', fontWeight: 700,
                      color: 'var(--navy)', fontSize: '1.1rem', marginBottom: '0.3rem',
                      letterSpacing: '-0.015em'
                    }}>
                      {hub.city}
                    </h4>
                    <p style={{
                      fontFamily: 'var(--font-heading)', fontWeight: 600,
                      color: 'var(--text-muted)', fontSize: '0.72rem', textTransform: 'uppercase',
                      letterSpacing: '0.5px', marginBottom: '1.2rem'
                    }}>
                      {hub.role}
                    </p>
                    <p style={{ color: 'var(--text-body)', fontSize: '0.8rem', lineHeight: '1.6', marginBottom: '1.5rem', textAlign: 'justify' }}>
                      {hub.address}
                    </p>
                  </div>
                  <div style={{ borderTop: '1px solid rgba(10,17,40,0.05)', paddingTop: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--navy)' }}>Email:</span>
                      <a href={`mailto:${hub.email}`} style={{ color: 'var(--text-body)', fontSize: '0.75rem', textDecoration: 'none' }} className="contact-link-tag">
                        {hub.email}
                      </a>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--navy)' }}>Phone:</span>
                      <a href={`tel:${hub.phone}`} style={{ color: 'var(--text-body)', fontSize: '0.75rem', textDecoration: 'none' }} className="contact-link-tag">
                        {hub.phone}
                      </a>
                    </div>
                  </div>
                </motion.div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>

        <div className="row g-4">
          {/* Business Hours */}
          <div className="col-lg-6">
            <AnimatedSection variant="fadeLeft">
              <div style={{
                background: '#FFFFFF',
                borderRadius: 2,
                padding: '2.5rem',
                border: '1px solid rgba(10,17,40,0.04)',
                boxShadow: 'var(--shadow-sm)'
              }}>
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div style={{
                    width: '36px', height: '36px', borderRadius: 2,
                    background: 'var(--orange-dim)', border: '1px solid var(--orange-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <FaClock size={13} color="var(--orange)" />
                  </div>
                  <h3 style={{
                    fontFamily: 'var(--font-heading)', fontWeight: 700,
                    color: 'var(--navy)', fontSize: '1.1rem', margin: 0,
                    letterSpacing: '-0.015em'
                  }}>
                    Business Hours
                  </h3>
                </div>

                {businessHours.map((b, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '1rem 0', borderBottom: i !== businessHours.length - 1 ? '1px solid rgba(10,17,40,0.06)' : 'none'
                  }}>
                    <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, color: 'var(--navy)', fontSize: '0.82rem' }}>
                      {b.label}
                    </span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                      {b.hours}
                    </span>
                  </div>
                ))}

                <div style={{
                  marginTop: '2rem',
                  background: 'var(--orange-dim)',
                  border: '1px solid var(--orange-border)',
                  borderRadius: 2,
                  padding: '1.2rem 1.5rem'
                }}>
                  <p style={{ color: 'var(--orange)', fontSize: '0.82rem', fontWeight: 700, margin: 0, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                    24/7 Emergency Support Available for Critical Shipments
                  </p>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Social & QR */}
          <div className="col-lg-6">
            <AnimatedSection variant="fadeRight">
              <div style={{
                background: 'var(--bg-dark)',
                borderRadius: 2,
                padding: '2.5rem',
                border: '1px solid rgba(255,255,255,0.05)',
                boxShadow: 'var(--shadow-lg)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <div>
                  <h3 style={{
                    fontFamily: 'var(--font-heading)', fontWeight: 700,
                    color: '#fff', fontSize: '1.1rem', marginBottom: '1.5rem',
                    letterSpacing: '-0.015em'
                  }}>
                    Connect With Us
                  </h3>

                  {/* Social links */}
                  <div className="mb-4">
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', marginBottom: '0.85rem' }}>
                      Follow our corporate activities
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                      {socialLinks.map((s, i) => (
                        <motion.a
                          key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                          aria-label={s.label}
                          whileHover={{ y: -2 }}
                          style={{
                            width: '36px', height: '36px', borderRadius: 2,
                            background: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'rgba(255,255,255,0.5)', transition: 'all 0.2s ease'
                          }}
                          className="contact-social-item"
                        >
                          <s.icon size={13} />
                        </motion.a>
                      ))}
                    </div>
                  </div>
                </div>

                {/* WhatsApp Direct Integration */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.08) 0%, rgba(7, 8, 13, 0.4) 100%)',
                  border: '1px solid rgba(37, 211, 102, 0.25)',
                  borderRadius: 4, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem',
                  boxShadow: '0 8px 32px rgba(37, 211, 102, 0.03)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '46px', height: '46px',
                      background: 'rgba(37, 211, 102, 0.1)', borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      border: '1px solid rgba(37, 211, 102, 0.2)'
                    }}>
                      <FaWhatsapp size={24} color="#25D366" />
                    </div>
                    <div>
                      <h5 style={{ color: '#fff', fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.9rem', margin: '0 0 0.15rem 0' }}>
                        Direct WhatsApp Support
                      </h5>
                      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', margin: 0, lineHeight: 1.4 }}>
                        Instant operational support and shipping assistance.
                      </p>
                    </div>
                  </div>
                  <motion.a
                    href="https://wa.me/917010755885?text=Hi%20ISAAC%20Logistics%2C%20I%20would%20like%20to%20inquire%20about%20your%20shipping%20and%20logistics%20services."
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    style={{
                      background: '#25D366',
                      color: '#07080D',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      textDecoration: 'none',
                      padding: '0.75rem 1.25rem',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 15px rgba(37, 211, 102, 0.2)'
                    }}
                  >
                    <FaWhatsapp size={16} /> Chat on WhatsApp
                  </motion.a>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>

        {/* Google Maps Embed */}
        <AnimatedSection variant="fadeUp" delay={0.2} className="mt-5">
          <div style={{
            borderRadius: 2, overflow: 'hidden',
            border: '1px solid rgba(10,17,40,0.04)',
            boxShadow: 'var(--shadow-md)',
            height: '450px', width: '100%'
          }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7057123.768720406!2d74.99177965!3d15.700276250000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8689d3f489cfd2fb%3A0x43f23aa04a543ff!2sISAAC%20Logistic%20Solutions%20India%20Private%20Limited!5e1!3m2!1sen!2sin!4v1784959677668!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              title="ISAAC Logistic Solutions Location Map"
            />
          </div>
        </AnimatedSection>
      </div>
    </section>
  </main>
  );
};

export default Contact;
